import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { menuStyles } from "../ui/MenuStyles";
import { navItemStyle, navItemActive } from "../ui/NavStyles";
import { CustomerNoticesContext } from "../CustomerLayout";
import { useCustomerRoles } from "../CustomerRolesContext";
import { ROLE_OPERATIONS, ROLE_VIEWER } from "../customerRoles";

export default function SlidesMenu() {
  const location = useLocation();
  const navigate = useNavigate();
  const { blocking } = useContext(CustomerNoticesContext);
  const { rolesReady, hasAnyRole } = useCustomerRoles();

  const isActive =
    location.pathname === "/slides" ||
    location.pathname.startsWith("/slides/");

  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    }

    function handleEsc(e) {
      if (e.key === "Escape") setOpen(false);
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEsc);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEsc);
    };
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const items = useMemo(() => {
    if (!rolesReady) return [];
    const out = [];
    if (hasAnyRole([ROLE_VIEWER])) {
      out.push({ label: "Lookup", path: "/slides" });
    }
    if (hasAnyRole([ROLE_OPERATIONS])) {
      out.push({
        label: "Upload",
        path: "/slides/upload",
        disabled: blocking,
      });
      out.push({ label: "Upload Activity", path: "/slides/upload-activity" });
    }
    return out;
  }, [rolesReady, hasAnyRole, blocking]);

  if (!rolesReady || items.length === 0) return null;

  return (
    <div ref={ref} style={{ position: "relative" }} data-tour="nav-slides">
      <button
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        style={{
          ...navItemStyle,
          ...menuStyles.trigger,
          ...(isActive ? navItemActive : {}),
        }}
      >
        Slides
        <span style={menuStyles.caret}>▾</span>
      </button>

      {open && (
        <div role="menu" style={menuStyles.popover}>
          {items.map((item) => (
            <button
              key={item.path}
              role="menuitem"
              disabled={item.disabled}
              style={{
                ...menuStyles.item,
                ...(item.disabled ? { opacity: 0.5, cursor: "not-allowed" } : {}),
              }}
              onClick={() => {
                if (item.disabled) return;
                navigate(item.path);
                setOpen(false);
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
